import React from 'react';
import { NavLink } from 'react-router-dom';
import { BarChart3, Building2, Contact, Handshake, Settings } from 'lucide-react';
import { Logo } from '../Logo';
import { UserMenu } from '../UserMenu';

const NAV = [
  { to: '/app', label: 'Dashboard', icon: <BarChart3 className="h-4 w-4" />, end: true },
  { to: '/contacts', label: 'Contacts', icon: <Contact className="h-4 w-4" /> },
  { to: '/companies', label: 'Companies', icon: <Building2 className="h-4 w-4" /> },
  { to: '/deals', label: 'Pipeline', icon: <Handshake className="h-4 w-4" /> },
  { to: '/reports', label: 'Reports', icon: <BarChart3 className="h-4 w-4" /> },
  { to: '/settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
];

export function CrmShell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  const maxWidth = wide ? 'max-w-[110rem]' : 'max-w-7xl';
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className={`mx-auto flex ${maxWidth} items-center justify-between px-4 py-4 sm:px-6 lg:px-8`}>
          <div className="flex items-center gap-2">
            <Handshake className="h-7 w-7 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
          </div>
          <UserMenu />
        </div>
      </header>
      <div className={`mx-auto flex ${maxWidth} flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:px-8`}>
        <aside className="lg:w-56">
          <nav className="flex gap-1 overflow-x-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:flex-col">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                    isActive ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function Button({
  children,
  variant = 'primary',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  const cls = variant === 'primary'
    ? 'bg-primary text-white hover:bg-primary-hover'
    : variant === 'danger'
      ? 'border border-red-200 bg-white text-red-700 hover:bg-red-50'
      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50';
  return (
    <button
      {...props}
      className={`rounded-md px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${cls} ${props.className || ''}`}
    >
      {children}
    </button>
  );
}

export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Close
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
